import { useUserProfileQuery, useValpayConfigQuery } from "@graphql/api";
import Error from "@hudson/ui/components/Error";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import Valpay, { type ValpaySubmitData } from "@hudson/ui/components/Valpay";
import { getProfiles } from "@lib/auth/jwt";
import userProfileAddValpayCc from "@lib/services/user_profile_add_valpay_cc";
import userProfileDeleteCc from "@lib/services/user_profile_delete_cc";
import Session from "@lib/session";
import useConfig from "@lib/use_config";
import useContent from "@lib/use_content";
import { useState } from "react";
import CreditCards from "./CreditCards";

export default function Payment() {
  const { data, loading, error } = useValpayConfigQuery();
  const { data: cfgData, loading: cfgLoading } = useConfig([
    "defaultCcProcessorId",
    "shopperReference",
    "showCczip",
  ]);
  const {
    data: cData,
    loading: cLoading,
    error: cError,
  } = useContent(["beErrProfileCcAdd", "beErrProfileCcRemove", "beProfileCcAddSubmitButton"]);
  const [valpaySeed, setValpaySeed] = useState(1); // Used to reload ValPay component
  const [errorMessage, setError] = useState("");

  const { data: profileData, loading: profileLoading, error: profileError } = useUserProfileQuery();

  if (error || profileError || cError) {
    console.error(error, profileError, cError);
    return null;
  }

  async function onDelete(id: string): Promise<boolean> {
    const userId = getProfiles().user;
    if (!userId) {
      console.error("No user ID found in session.");
      setError(cData?.content.beErrProfileCcRemove);
      return false;
    }
    const result = await userProfileDeleteCc(undefined, id);
    if (!result) setError(cData?.content.beErrProfileCcRemove);
    return result;
  }

  async function onSubmit(
    {
      name,
      encryptedCardNumber,
      encryptedExpiryMonth,
      encryptedExpiryYear,
      encryptedSecurityCode,
      postalCode,
    }: ValpaySubmitData,
    onResult: (success: boolean) => void,
  ) {
    setError("");

    // Get the shopperReference to pass to the mutation for proper cache invalidation
    const shopperReference = Session.getBooking().payment?.shopperReference || cfgData?.config.shopperReference;

    try {
      const result = await userProfileAddValpayCc(
        encryptedCardNumber,
        encryptedExpiryMonth,
        encryptedExpiryYear,
        encryptedSecurityCode,
        name,
        postalCode,
        shopperReference,
      );
      if (!result) {
        setError(cData?.content.beErrProfileCcAdd);
      }
      onResult(!!result);
      setValpaySeed(valpaySeed + 1);
    } catch (e) {
      console.error(e);
      setError(cData?.content.beErrProfileCcAdd);
      onResult(false);
    }
  }

  let content;
  if (data?.valpayConfig.enabled) {
    const shopperReference = Session.getBooking().payment?.shopperReference || cfgData?.config.shopperReference;

    content = (
      <>
        <Error className="mt-8" message={errorMessage} heavyStyle />
        <Valpay
          key={valpaySeed}
          buttonLabel={cData?.content.beProfileCcAddSubmitButton}
          className="mt-8"
          processorId={cfgData?.config.defaultCcProcessorId}
          profileMode={true}
          onDelete={onDelete}
          onSubmit={onSubmit}
          shopperReference={shopperReference}
          showZip={cfgData?.config.showCczip}
        />
      </>
    );
  } else {
    content = <CreditCards cards={profileData?.userProfile?.creditCards?.filter(Boolean) as be.TokenizedCreditCard[] || []} />;
  }

  return (
    <>
      {loading || profileLoading || cfgLoading || cLoading ? (
        <ConcentricLoader className="max-w-2xl px-4 mx-auto opacity-60 sm:py-10 sm:px-6 lg:px-8" />
      ) : (
        content
      )}
    </>
  );
}
